import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useAnnouncementModals, markSeen } from './useAnnouncementModals';
import DesignGuideModal from '../pass-templates-new/modals/DesignGuideModal';
import googlePassImage from '../../../assets/images/announcements/google-pass-update.svg';
import applePassImage from '../../../assets/images/announcements/apple-pass-update.svg';

const AnnouncementModal = () => {
  const navigate = useNavigate();
  const { accountId, announcements } = useAnnouncementModals();
  const [isOpen, setIsOpen] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const {
    isOpen: isDesignGuideOpen,
    onOpen: onOpenDesignGuide,
    onClose: onCloseDesignGuide,
  } = useDisclosure();

  useEffect(() => {
    [googlePassImage, applePassImage].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const showAnnouncement = accountId && announcements.length > 0 && isOpen;

  const total = announcements.length;
  const safeIndex = Math.min(currentIndex, total - 1);
  const current = announcements[safeIndex] ?? null;
  const Content = current?.Content;
  const isLast = safeIndex === total - 1;
  const isOnly = total === 1;

  const dismissAll = () => {
    announcements.forEach((a) => markSeen(accountId!, a.id));
    setIsOpen(false);
  };

  const handleNext = () => {
    if (safeIndex < total - 1) {
      setCurrentIndex(safeIndex + 1);
    }
  };

  const handleBack = () => {
    if (safeIndex > 0) {
      setCurrentIndex(safeIndex - 1);
    }
  };

  const handleCta = () => {
    announcements.forEach((a) => markSeen(accountId!, a.id));
    const route = isOnly && current?.standaloneCtaRoute
      ? current.standaloneCtaRoute
      : current?.ctaRoute;
    if (route) navigate(route);
    setIsOpen(false);
  };

  if (!showAnnouncement && !isDesignGuideOpen) return null;

  return (
    <>
    {showAnnouncement && current && Content && (
    <Modal isOpen onClose={dismissAll} isCentered closeOnOverlayClick={false} blockScrollOnMount={!isDesignGuideOpen}>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        borderRadius="20px"
        overflow="hidden"
        bg="white"
        maxW="560px"
        mx={4}
      >
        <ModalBody p={0} minH="680px">
          <Box
            backgroundImage="linear-gradient(112deg, #FF9554 22%, #FF4935 83%)"
            backgroundSize="100% 198px"
            backgroundRepeat="no-repeat"
            position="relative"
          >
            <Box px={8} pt={6} pb={2}>
              <Text
                textStyle="bodyFamilySemiBold"
                fontSize="12px"
                letterSpacing="2px"
                textTransform="uppercase"
                color="white"
                textAlign="center"
                mb={2}
              >
                What&apos;s new
              </Text>
            </Box>

            {(isLast || isOnly) && (
              <Box
                as="button"
                onClick={dismissAll}
                position="absolute"
                top={2}
                right={4}
                color="white"
                opacity={0.8}
                _hover={{ opacity: 1 }}
                p={1}
              >
                <CloseIcon boxSize="12px" />
              </Box>
            )}

            <Content
              dots={
                total > 1 ? (
                  <Flex justify="center" gap="6px" mt={3}>
                    {announcements.map((_, i) => (
                      <Box
                        key={i}
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg="white"
                        opacity={i === safeIndex ? 1 : 0.4}
                      />
                    ))}
                  </Flex>
                ) : undefined
              }
            />
          </Box>

          <Flex direction="column" align="center" px={10} pb={8}>
            {isOnly ? (
              <>
                <Button
                  onClick={handleCta}
                  variant="gradient"
                  w="full"
                  maxW="420px"
                  h="43px"
                  mb={3}
                >
                  {current.standaloneCtaLabel || current.ctaLabel}
                </Button>
                <Text
                  as="button"
                  onClick={onOpenDesignGuide}
                  textStyle="bodyFamilySemiBold"
                  fontSize="14px"
                  color="inkBlue"
                  cursor="pointer"
                  _hover={{ color: 'orangePrimary' }}
                  py={2}
                >
                  See the design guide &rarr;
                </Text>
              </>
            ) : isLast ? (
              <>
                <Button
                  onClick={handleCta}
                  variant="gradient"
                  w="full"
                  maxW="420px"
                  h="43px"
                  mb={3}
                >
                  {current.ctaLabel}
                </Button>
                <Text
                  as="button"
                  onClick={handleBack}
                  textStyle="bodyFamilySemiBold"
                  fontSize="14px"
                  color="inkBlue"
                  cursor="pointer"
                  _hover={{ color: 'orangePrimary' }}
                  py={2}
                >
                  Back
                </Text>
              </>
            ) : (
              <Button
                onClick={handleNext}
                variant="gradient"
                w="full"
                maxW="420px"
                h="43px"
              >
                Next
              </Button>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
    )}
    {isDesignGuideOpen && (
      <DesignGuideModal isOpen onClose={onCloseDesignGuide} />
    )}
    </>
  );
};

export default AnnouncementModal;
